import React, { useState, useEffect } from 'react';
import './Connections.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Connections = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    connectionType: 'saml_custom',
    idpMetadataUrl: '',
    attributes: {
      ID: '',
      Email: '',
      FirstName: '',
      LastName: ''
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentConnectionId, setCurrentConnectionId] = useState(null);

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${societyId}/connections?access_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch connections');
      }

      const data = await response.json();
      setConnections(data.data?.Data || []);
    } catch (error) {
      console.error('Error fetching connections:', error);
      setError('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  const handleStatusToggle = async (connection) => {
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${societyId}/connections/${connection.Id}/status?access_token=${encodeURIComponent(token)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            Active: !connection.IsActive
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update connection status');
      }

      // Refresh connections list
      await fetchConnections();
    } catch (error) {
      console.error('Error updating connection status:', error);
      setError('Failed to update connection status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      connectionType: 'saml_custom',
      idpMetadataUrl: '',
      attributes: {
        ID: '',
        Email: '',
        FirstName: '',
        LastName: ''
      }
    });
    setIsEditing(false);
    setCurrentConnectionId(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleAddOrUpdate = () => {
    setIsModalOpen(true);
    resetForm();
  };

  const handleEdit = (connection) => {
    setFormData({
      name: connection.Name,
      domain: connection.Domain,
      connectionType: connection.ConnectionType,
      idpMetadataUrl: connection.IDPMetadataUrl,
      attributes: {
        ID: connection.Attributes?.ID || '',
        Email: connection.Attributes?.Email || '',
        FirstName: connection.Attributes?.FirstName || '',
        LastName: connection.Attributes?.LastName || ''
      }
    });
    setCurrentConnectionId(connection.Id);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      if (isEditing) {
        // Update connection
        const updatePayload = {
          IdpMetadataUrl: formData.idpMetadataUrl,
          Attributes: formData.attributes
        };

        const response = await fetch(
          `${API_URL}/api/v1/auth/orgs/${societyId}/connections/${currentConnectionId}?access_token=${encodeURIComponent(token)}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updatePayload)
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update connection');
        }
      } else {
        // Create new connection
        const createPayload = {
          Name: formData.name,
          Domain: formData.domain
        };

        const response = await fetch(
          `${API_URL}/api/v1/auth/orgs/${societyId}/connections?access_token=${encodeURIComponent(token)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(createPayload)
          }
        );

        if (!response.ok) {
          throw new Error('Failed to create connection');
        }
      }

      await fetchConnections();
      handleModalClose();
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('attr_')) {
      // Handle attribute fields
      const attrName = name.replace('attr_', '');
      setFormData(prev => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [attrName]: value
        }
      }));
    } else {
      // Handle other fields
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <div className="connections-container">
      <div className="connections-header">
        <h2>SAML Connections</h2>
        {connections.length > 0 ? (
          <button
            className="update-connection-btn"
            onClick={() => handleEdit(connections[0])}
          >
            <i className="fas fa-edit"></i>
            Update Connection
          </button>
        ) : (
          <button
            className="create-connection-btn"
            onClick={handleAddOrUpdate}
          >
            <i className="fas fa-plus"></i>
            Add Connection
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="connections-grid">
        {connections.map((connection) => (
          <div key={connection.Id} className="connection-card">
            <div className="connection-header">
              <h3>{connection.Name}</h3>
              <div className="status-controls">
                <span className={`status-badge ${connection.IsActive ? 'active' : 'inactive'}`}>
                  {connection.IsActive ? 'Active' : 'Inactive'}
                </span>
                <button
                  className={`toggle-button ${connection.IsActive ? 'active' : 'inactive'}`}
                  onClick={() => handleStatusToggle(connection)}
                  title={connection.IsActive ? 'Deactivate Connection' : 'Activate Connection'}
                >
                  <div className="toggle-slider"></div>
                </button>
              </div>
            </div>

            <div className="connection-details">
              <div className="detail-group">
                <h4>General Information</h4>
                <p><strong>Type:</strong> {connection.ConnectionType}</p>
                <p><strong>Domain:</strong> {connection.Domain}</p>
                <p><strong>Created:</strong> {formatDate(connection.CreatedDate)}</p>
                <p><strong>Last Modified:</strong> {formatDate(connection.ModifiedDate)}</p>
              </div>

              <div className="detail-group">
                <h4>Service Provider Details</h4>
                <p><strong>Entity ID:</strong> {connection.EntityId}</p>
                <p>
                  <strong>Metadata URL:</strong>
                  <a href={connection.MetadataUrl} target="_blank" rel="noopener noreferrer">
                    View Metadata
                  </a>
                </p>
                <p>
                  <strong>ACS Endpoint:</strong>
                  <span className="endpoint-url">{connection.ACSEndpoint}</span>
                </p>
                <div className="certificate-section">
                  <h5>SP Certificate</h5>
                  <button 
                    className="view-cert-btn"
                    onClick={() => alert(connection.SPCertificate.Certificate)}
                  >
                    View Certificate
                  </button>
                </div>
              </div>

              <div className="detail-group">
                <h4>Identity Provider Details</h4>
                <p><strong>Entity ID:</strong> {connection.IDPEntityId}</p>
                <p><strong>Login URL:</strong> {connection.IDPLoginUrl}</p>
                <p><strong>Logout URL:</strong> {connection.IDPLogoutUrl}</p>
                <p>
                  <strong>Metadata URL:</strong>
                  <a href={connection.IDPMetadataUrl} target="_blank" rel="noopener noreferrer">
                    View IDP Metadata
                  </a>
                </p>
                <div className="certificate-section">
                  <h5>IDP Certificate</h5>
                  <p>Valid from: {formatDate(connection.IDPCertificate.NotBefore)}</p>
                  <p>Valid until: {formatDate(connection.IDPCertificate.NotAfter)}</p>
                  <button 
                    className="view-cert-btn"
                    onClick={() => alert(connection.IDPCertificate.Certificate)}
                  >
                    View Certificate
                  </button>
                </div>
              </div>

              {connection.Attributes && (
                <div className="detail-group">
                  <h4>Attribute Mapping</h4>
                  <p><strong>ID:</strong> {connection.Attributes.ID}</p>
                  <p><strong>Email:</strong> {connection.Attributes.Email}</p>
                  <p><strong>First Name:</strong> {connection.Attributes.FirstName}</p>
                  <p><strong>Last Name:</strong> {connection.Attributes.LastName}</p>
                  {connection.Attributes.Group && (
                    <p><strong>Group:</strong> {connection.Attributes.Group}</p>
                  )}
                  {connection.Attributes.CustomMapping && (
                    <p><strong>Custom Mapping:</strong> {connection.Attributes.CustomMapping}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {connections.length === 0 && !loading && (
        <div className="no-connections">
          <i className="fas fa-plug"></i>
          <p>No SAML connections configured</p>
        </div>
      )}

      {loading && (
        <div className="loading-connections">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading connections...</p>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditing ? 'Update Connection' : 'Add Connection'}</h3>
              <button 
                className="close-button"
                onClick={handleModalClose}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {!isEditing ? (
                // Create connection form fields
                <>
                  <div className="form-group">
                    <label htmlFor="name">Connection Name*</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter connection name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="domain">Domain*</label>
                    <input
                      type="text"
                      id="domain"
                      name="domain"
                      value={formData.domain}
                      onChange={handleChange}
                      required
                      placeholder="Enter domain (e.g., company.com)"
                    />
                  </div>
                </>
              ) : (
                // Update connection form fields
                <>
                  <div className="form-group">
                    <label htmlFor="idpMetadataUrl">IDP Metadata URL*</label>
                    <input
                      type="url"
                      id="idpMetadataUrl"
                      name="idpMetadataUrl"
                      value={formData.idpMetadataUrl}
                      onChange={handleChange}
                      required
                      placeholder="Enter IDP Metadata URL"
                    />
                  </div>

                  <div className="form-section">
                    <h4>Attribute Mapping</h4>
                    <div className="form-group">
                      <label htmlFor="attr_ID">ID Attribute*</label>
                      <input
                        type="text"
                        id="attr_ID"
                        name="attr_ID"
                        value={formData.attributes.ID}
                        onChange={handleChange}
                        required
                        placeholder="Enter ID attribute"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="attr_Email">Email Attribute*</label>
                      <input
                        type="text"
                        id="attr_Email"
                        name="attr_Email"
                        value={formData.attributes.Email}
                        onChange={handleChange}
                        required
                        placeholder="Enter Email attribute"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="attr_FirstName">First Name Attribute*</label>
                      <input
                        type="text"
                        id="attr_FirstName"
                        name="attr_FirstName"
                        value={formData.attributes.FirstName}
                        onChange={handleChange}
                        required
                        placeholder="Enter First Name attribute"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="attr_LastName">Last Name Attribute*</label>
                      <input
                        type="text"
                        id="attr_LastName"
                        name="attr_LastName"
                        value={formData.attributes.LastName}
                        onChange={handleChange}
                        required
                        placeholder="Enter Last Name attribute"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={handleModalClose}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                >
                  {isEditing ? 'Update Connection' : 'Create Connection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Connections; 